import {
  Node as ReactFlowNode,
  Edge as ReactFlowEdge,
  MarkerType
} from 'reactflow';

import { useTrackStore } from '~/scenes/Build/scenes/FlowBuilder/stores';
import {
  useCanvasStore,
  CanvasNode,
  CanvasEdge
} from '~/scenes/Build/scenes/FlowBuilder/stores/canvasStore';
import { useComponentConfigStore } from '~/scenes/Build/scenes/FlowBuilder/stores/componentConfigStore';

/**
 * Convert Canvas stores to ReactFlow format
 *
 * This utility reads directly from the Canvas stores (nodes, edges)
 * and converts them to the format expected by ReactFlow.
 *
 * Key differences from legacy convertSpec:
 * - Reads from Zustand stores instead of canvasSpec prop
 * - Uses CanvasNode/CanvasEdge types instead of legacy format
 * - Tags are already normalized in the stores
 * - Positions will be calculated by formatReactFlowNodesPositions
 */

type ConversionResult = {
  nodes: ReactFlowNode[];
  edges: ReactFlowEdge[];
};

const getComponentTags = (componentId: string): string[] => {
  const componentConfigStore = useComponentConfigStore.getState();
  const config = componentConfigStore.getConfig(componentId);

  if (!config) {
    return ['component'];
  }

  const tags: string[] = [];

  tags.push('component');

  if (config.type === 'choice-connector') {
    tags.push('choice');
  } else if (config.type === 'parallel-execution-connector') {
    tags.push('parallel-execution-connector');
  } else if (
    config.type === 'block-execution-connector' ||
    config.type === 'for-each-connector' ||
    config.type === 'event-processor-connector'
  ) {
    tags.push('compound');
  }

  if (
    componentId === 'trigger' ||
    componentId === 'start-track' ||
    componentId === 'starter-node'
  ) {
    tags.push('starter');
  }

  return tags;
};

const getEdgeTags = (
  sourceId: string,
  edgeMetadata?: {
    conditionType?: 'when' | 'otherwise';
    label?: string;
    description?: string;
    executionId?: string;
  }
): string[] => {
  const tags: string[] = ['default'];

  const componentConfigStore = useComponentConfigStore.getState();
  const sourceConfig = componentConfigStore.getConfig(sourceId);

  if (sourceConfig?.type === 'choice-connector') {
    tags.push('choice');
    if (edgeMetadata?.conditionType === 'when') {
      tags.push('when');
    } else if (edgeMetadata?.conditionType === 'otherwise') {
      tags.push('otherwise');
    }
  } else if (sourceConfig?.type === 'parallel-execution-connector') {
    tags.push('parallel-edge');
  }

  return tags;
};

const isSubLevelTrack = (nodeId: string) => {
  const { tracks } = useTrackStore.getState();
  const track = Array.from(tracks.values()).find(t =>
    t.componentIds.includes(nodeId)
  )?.name;
  return (
    track?.endsWith('-onProcessTrack') || track?.endsWith('-onExceptionTrack')
  );
};

const isNodeDeletable = (nodeId: string, deletable?: boolean): boolean => {
  if (isSubLevelTrack(nodeId)) {
    return true;
  }
  if (nodeId.startsWith('api-first') || nodeId.startsWith('mcp-server')) {
    return false;
  }
  return deletable ?? true;
};

const isEdgeDeletable = (
  sourceId: string,
  targetId: string,
  deletable?: boolean
): boolean => {
  const isSourceProtected =
    sourceId.startsWith('api-first') || sourceId.startsWith('mcp-server');
  const isTargetProtected =
    targetId.startsWith('api-first') || targetId.startsWith('mcp-server');

  if (isSourceProtected || isTargetProtected) {
    return false;
  }

  return deletable ?? true;
};

export const convertCanvasToReactFlow = (
  nodesMap?: Map<string, CanvasNode>,
  edgesMap?: Map<string, CanvasEdge>,
  selectedNodeIds?: Set<string>,
  selectedEdgeIds?: Set<string>
): ConversionResult => {
  const canvasStore = useCanvasStore.getState();

  // Use provided maps/sets or fall back to entire store
  const nodes = nodesMap || canvasStore.nodes;
  const edges = edgesMap || canvasStore.edges;
  const selectNodeIds = selectedNodeIds || canvasStore.selectedNodeIds;
  const selectEdgeIds = selectedEdgeIds || canvasStore.selectedEdgeIds;

  const convertedNodes: ReactFlowNode[] = [];
  nodes.forEach(node => {
    const tags = node.tags.length > 0 ? node.tags : getComponentTags(node.id);
    convertedNodes.push({
      id: node.id,
      data: {
        tags
      },
      selected: selectNodeIds.has(node.id),
      width: node.width,
      height: node.height,
      deletable: isNodeDeletable(node.id, node.deletable),
      position: node.position || { x: 0, y: 0 }
    });
  });

  const edgeIdsSet = new Set<string>();
  const convertedEdges: ReactFlowEdge[] = [];

  edges.forEach(edge => {
    if (edgeIdsSet.has(edge.id)) {
      return;
    }

    edgeIdsSet.add(edge.id);

    const tags =
      edge.tags.length > 0
        ? edge.tags
        : getEdgeTags(edge.sourceId, edge.metadata);

    convertedEdges.push({
      id: edge.id,
      source: edge.sourceId,
      target: edge.targetId,
      selected: selectEdgeIds.has(edge.id),
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: 'black'
      },
      data: {
        tags
      },
      deletable: isEdgeDeletable(edge.sourceId, edge.targetId, edge.deletable)
    });
  });

  return {
    nodes: convertedNodes,
    edges: convertedEdges
  };
};
